import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, FileSpreadsheet, Loader2, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatEuro, formatPct } from "@/lib/format";
import { toast } from "sonner";
import ConflictResolverModal from "@/components/events/ConflictResolverModal";

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-medium mt-0.5 truncate">{value || "—"}</div>
  </div>
);

export default function XLSXUploader({ onSaved }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx") && !file.name.toLowerCase().endsWith(".xlsm")) {
      toast.error("Apenas ficheiros .xlsx são suportados");
      return;
    }
    setParsing(true);
    setPreview(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/events/parse", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.parsed);
      if (data.conflict?.exists) setConflict(data.conflict);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao processar ficheiro");
    } finally {
      setParsing(false);
    }
  };

  const doSave = async (mode = "new", existingId = null) => {
    if (!preview) return;
    setSaving(true);
    try {
      const payload = { ...preview, mode, existing_id: existingId };
      delete payload.source_filename;
      await api.post("/events", payload);
      toast.success(mode === "update" ? "Processo atualizado" : "Evento importado com sucesso");
      setPreview(null);
      setConflict(null);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onConfirm = () => {
    if (conflict?.exists) return; // handled via modal
    doSave("new");
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileSpreadsheet className="h-5 w-5 text-primary" />
        <h2 className="font-display text-base sm:text-lg font-bold tracking-tight">Importar Orçamento (.xlsx)</h2>
      </div>

      {!preview && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="xlsx-dropzone"
          className={`cursor-pointer rounded-xl border-2 border-dashed transition-colors p-8 sm:p-10 flex flex-col items-center justify-center text-center gap-3
            ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/40"}`}>
          <input ref={inputRef} type="file" accept=".xlsx,.xlsm" className="hidden"
            data-testid="xlsx-dropzone-input"
            onChange={(e) => handleFile(e.target.files[0])} />
          {parsing ? (
            <>
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <p className="text-sm font-medium">A processar ficheiro...</p>
            </>
          ) : (
            <>
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <UploadCloud className="h-7 w-7 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">Arraste o ficheiro aqui ou clique para selecionar</p>
                <p className="text-xs text-muted-foreground mt-1">Modelo "Orçamento Eventos" — lê Cliente, Data, Local, Projeto, Nº Processo, Valores</p>
              </div>
            </>
          )}
        </div>
      )}

      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="rounded-xl border border-border bg-muted/30 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-semibold">Pré-visualização</span>
                </div>
                <button onClick={() => { setPreview(null); setConflict(null); }}
                  className="p-1.5 rounded-lg hover:bg-muted" data-testid="preview-close-button">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {preview.fecho_empty && (
                <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50 px-4 py-3 text-sm text-amber-800 dark:text-amber-300" data-testid="fecho-empty-warning">
                  ⚠ A folha <strong>"Fecho de Processo"</strong> parece estar vazia (valores a 0). Pode importar na mesma, mas confirme se o ficheiro está preenchido.
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                <Field label="Data" value={preview.data_evento} />
                <Field label="Cliente" value={preview.cliente} />
                <Field label="Local" value={preview.local} />
                <Field label="Projeto" value={preview.nome_projeto} />
                <Field label="Nº Processo" value={preview.numero_processo} />
                <Field label="Valor de Venda" value={formatEuro(preview.valor_total)} />
                <Field label="Total de Custos" value={formatEuro(preview.custos)} />
                <Field label="Custos Indiretos" value={`${formatEuro(preview.custos_indiretos)} (${formatPct(preview.custos_indiretos_pct)})`} />
                <Field label="Margem" value={`${formatEuro(preview.margem)} (${formatPct(preview.margem_pct)})`} />
                <Field label="Lucro Mundideias" value={`${formatEuro(preview.lucro_comercial_valor)} (${formatPct(preview.lucro_comercial_pct)})`} />
                <Field label="Lucro Comercial 1" value={`${formatEuro(preview.comercial_1_valor)} (${formatPct(preview.comercial_1_pct)})`} />
                <Field label="Lucro Comercial 2" value={`${formatEuro(preview.comercial_2_valor)} (${formatPct(preview.comercial_2_pct)})`} />
                <Field label="Lucro Comercial 3" value={`${formatEuro(preview.comercial_3_valor)} (${formatPct(preview.comercial_3_pct)})`} />
              </div>
              {conflict?.exists && (
                <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/50 px-4 py-3 text-sm text-amber-800 dark:text-amber-300" data-testid="conflict-warning-inline">
                  ⚠ Já existe um processo com o nº <strong>{preview.numero_processo}</strong>. Escolha como proceder ao confirmar.
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setPreview(null); setConflict(null); }} data-testid="preview-cancel-button">
                  Cancelar
                </Button>
                {conflict?.exists ? (
                  <Button onClick={() => setConflict({ ...conflict, open: true })} disabled={saving}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="preview-resolve-button">
                    Resolver conflito
                  </Button>
                ) : (
                  <Button onClick={onConfirm} disabled={saving} data-testid="xlsx-upload-submit-button">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar e Adicionar"}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConflictResolverModal
        open={!!conflict?.open}
        conflict={conflict}
        preview={preview}
        saving={saving}
        onClose={() => setConflict(conflict ? { ...conflict, open: false } : null)}
        onOverwrite={() => doSave("update", conflict?.existing_id)}
        onCreateNew={() => doSave("new")}
      />
    </div>
  );
}
